import { useRef, useState } from "react";
import { Upload, Loader2, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LeadImportPreview } from "@/components/leads/LeadImportPreview";
import { api, errMsg } from "@/lib/api";

const ImportResult = ({ res }) => (
  <div className="space-y-2 rounded-xl bg-emerald-50 p-4 text-sm" data-testid="lead-import-result">
    <div className="flex items-center gap-2 font-semibold text-emerald-800"><CheckCircle2 className="h-4 w-4" />{res.added} new leads added</div>
    <div className="text-emerald-900/80" data-testid="lead-import-dups">{res.duplicates} already in the system (skipped) · {res.total} rows read{res.added > 0 ? ` · ${res.pinned} of ${res.added} new leads have a map pin` : ""}{res.by_area > 0 ? ` · ${res.by_area} went to their area owner` : ""}</div>
    {res.invalid.length > 0 && <div className="text-rose-700" data-testid="lead-import-invalid">{res.invalid.length} rows skipped: {res.invalid.slice(0, 5).map((r) => `row ${r.line}${r.name ? ` (${r.name})` : ""}`).join(", ")}{res.invalid.length > 5 ? "..." : ""}</div>}
  </div>
);

const Picker = ({ busy, onPick }) => (
  <button type="button" disabled={busy} onClick={onPick} className="flex w-full flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-[#E7E2D6] bg-[#FBF9F4] p-8 text-sm transition-colors hover:border-[#D96B43]" data-testid="lead-import-pick">
    {busy ? <Loader2 className="h-6 w-6 animate-spin text-[#D96B43]" /> : <Upload className="h-6 w-6 text-[#D96B43]" />}
    <span className="font-semibold">{busy ? "Checking rows..." : "Choose responses file"}</span>
    <span className="text-xs text-muted-foreground">.xlsx, .xls, .ods or .csv, max 5 MB. You will see every row before anything is saved.</span>
  </button>
);

const TemplateLink = () => (
  <a href="/templates/NexFresh_Leads_Template.csv" download className="flex items-center justify-between gap-3 rounded-xl border border-[#E7E2D6] px-4 py-3 text-sm transition-colors hover:border-[#D96B43]" data-testid="lead-template-download">
    <span><span className="font-semibold">No form export?</span> <span className="text-muted-foreground">Download the CSV template, paste your rows under the headings (keep row 1 as is, delete the EXAMPLE row) and upload it. Only Full Name and Contact Number are required.</span></span>
    <Download className="h-4 w-4 shrink-0 text-[#D96B43]" />
  </a>
);

export const LeadImportDialog = ({ open, onClose, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [res, setRes] = useState(null);
  const reset = () => { setFile(null); setPreview(null); setRes(null); if (ref.current) ref.current.value = ""; };
  const close = () => { reset(); onClose(); };
  const check = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true); setRes(null);
    try { const r = await api.post("/leads/import/preview", fd); setFile(f); setPreview(r.data); }
    catch (e) { toast.error(errMsg(e)); reset(); } finally { setBusy(false); }
  };
  const commit = async () => {
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const r = await api.post("/leads/import", fd); setRes(r.data); setPreview(null); toast.success(`${r.data.added} new leads added`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="lead-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Import Google Form responses</DialogTitle>
          <DialogDescription>In Google Forms open Responses, then "Link to Sheets". In the sheet choose File, Download, Microsoft Excel (.xlsx) or CSV, and upload it here (.xls and .ods also work). Anyone already in the system (same phone) is skipped.</DialogDescription>
        </DialogHeader>
        <input ref={ref} type="file" accept=".xlsx,.xls,.ods,.csv" className="hidden" onChange={(e) => check(e.target.files?.[0])} data-testid="lead-import-file" />
        {!preview && !res && <><TemplateLink /><Picker busy={busy} onPick={() => ref.current?.click()} /></>}
        {preview && <><div className="text-sm" data-testid="lead-preview-file"><span className="font-semibold">{file?.name}</span> · {preview.total} rows checked. Nothing is saved yet.</div><LeadImportPreview data={preview} /></>}
        {res && <ImportResult res={res} />}
        <div className="flex flex-wrap justify-end gap-2">
          {preview && <Button variant="outline" disabled={busy} onClick={() => { reset(); ref.current?.click(); }} data-testid="lead-preview-other-file">Choose another file</Button>}
          {preview && <Button disabled={busy || preview.new === 0} onClick={commit} className="bg-[#0F382C] hover:bg-[#134E3A]" data-testid="lead-preview-confirm">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}{preview.new === 0 ? "No new leads to import" : `Import ${preview.new} new lead${preview.new === 1 ? "" : "s"}`}</Button>}
          {!preview && <Button variant="outline" onClick={close} data-testid="lead-import-close">Done</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
